<template>
  <v-container fluid :class="drawer ? 'drawer-open' : 'drawer-closed'">
    <v-row align="start" justify="start">
      <v-col cols="12">
        <v-data-table
          class="trigger-table-display"
          :class="{'not-default-page-size': !this.isDefaultSize}"
          :headers="headers"
          :items="displayTriggers"
          :loading="currentPageLoading || !tableInited"
          :server-items-length="totalCount"
          :options="{
            page: pageIndex,
            itemsPerPage: pageSize,
            sortBy: sort.sortBy,
            sortDesc: sort.sortDesc,
          }"
          @update:options="paginate($event)"
          :footer-props="footerProps"
          multi-sort
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title class="d-flex align-center">
                {{ $localizationService.localize("trigger_page.title") }}
                <TableSettings
                  class="ml-5"
                  :pIsVisible="tableInited && !isDefaultSettings"
                  :pSaveButtonTooltip="$localizationService.localize('trigger_page.settings_tooltip.save')"
                  :pDefaultButtonTooltip=
                    "$localizationService.localize('trigger_page.settings_tooltip.apply_default')"
                  @onSaveSettings="saveSettings()"
                  @onApplyDefaultSettings="applyDefaultSettings()"
                />
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-text-field
                v-model="searchString"
                :label="$localizationService.localize('trigger_page.search')"
                class="search-field"
                hide-details
                @input="onInputSearchString()"
              ></v-text-field>
              <v-btn
                color="primary"
                class="ml-2"
                @click="openEditTriggerDialog()"
              >
                {{ $localizationService.localize("trigger_page.new_btn") }}
              </v-btn>
              <TriggerEditDialog ref="triggerEditDialog" />
              <ConfirmationDialog ref="deleteDialog" />
            </v-toolbar>
          </template>

          <template v-for="header in headers" v-slot:[`header.${header.value}`]>
            <span
              :key="header.value"
              :class="{
                'not-default-header-sort': isNotDefaultSortItems.includes(header.value)
              }"
            >
              {{ header.text }}
            </span>
          </template>

          <template v-slot:[`item.actionDraftTemplates`]="{ item }">
            <div>
              {{
                item.actionDraftTemplates
                  .map((it) => it.type.text)
                  .join(", ")
              }}
            </div>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex justify-end">
              <v-icon
                medium
                class="mr-2"
                @click="openEditTriggerDialog(item)"
              >mdi-pencil</v-icon>
              <v-icon
                medium
                color="red"
                @click="openDeleteTriggerDialog(item)"
              >mdi-delete</v-icon>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Vue from "vue";
import { mapGetters, mapState } from "vuex";
import lodash from "lodash";
import { getModule } from "vuex-module-decorators";
import { momentDateAndTime } from "@/utils/utils";
import ConfirmationDialog from "@/components/ConfirmationDialog.vue";
import TableSettings from "@/components/TableSettings.vue";
import tableSortParserMixin from "@/utils/table-sort-parser";
import TriggersModule from "../../store/triggers.store";
import TriggerEditDialog from "./trigger-edit-dialog/TriggerEditDialog.vue";

const locService = Vue.prototype.$localizationService;

export default {
  name: "Triggers",

  mixins: [tableSortParserMixin],

  components: {
    TriggerEditDialog,
    ConfirmationDialog,
    TableSettings,
  },

  data: () => ({
    tableInited: false,

    footerProps: {
      "items-per-page-options": [25, 50, 100],
    },

    searchString: "",
    invitationInProgess: {},

    headers: [
      {
        text: locService.localize("trigger_page.header.name"),
        value: "name",
        sortable: true,
      },
      {
        text: locService.localize("trigger_page.header.priority"),
        value: "priority",
        sortable: true,
      },
      {
        text: locService.localize("trigger_page.header.action_draft_templates"),
        value: "actionDraftTemplates",
        sortable: true,
      },
      {
        text: locService.localize("trigger_page.header.actions"),
        value: "actions",
        align: "end",
        sortable: false,
      },
    ],
  }),

  computed: {
    ...mapState("triggersModule", [
      "currentPageLoading",
      "currentPage",
      "displayTriggers",
      "totalCount",
    ]),
    ...mapState("coreModule", ["drawer"]),
    ...mapState("triggerTableSettingsModule", [
      "pageIndex",
      "pageSize",
      "sort",
      "defaultSettings",
    ]),
    ...mapGetters("triggerTableSettingsModule", [
      "isDefaultSettings",
      "isDefaultSort",
      "isDefaultSize",
    ]),

    isNotDefaultSortItems() {
      return !this.isDefaultSort
        ? this.getNotDefaultTableSortItems("sort")
        : [];
    },
  },

  methods: {
    momentDateAndTime,

    paginate(data) {
      this.$store.commit("triggerTableSettingsModule/changeSettings", {
        index: data.page,
        size: data.itemsPerPage,
        sort: {
          sortBy: [...data.sortBy],
          sortDesc: [...data.sortDesc],
        },
      });

      this.updateRoutePath();

      this.loadPage({
        index: data.page - 1,
        size: data.itemsPerPage,
        sort: data.sortBy.map((it, i) => (data.sortDesc[i] ? `-${it}` : it)),
        searchString: this.searchString,
      });
    },

    loadPage: lodash.debounce(async function (currentPage) {
      await this.$store.dispatch("triggersModule/loadPage", currentPage);
      this.tableInited = true;
    }, 500),

    saveSettings() {
      this.$store.commit("triggerTableSettingsModule/saveSettings");

      Vue.toasted.success(
        locService.localize("trigger_page.settings_msg.save"),
        { duration: 3000 },
      );
    },

    applyDefaultSettings() {
      this.$store.commit("triggerTableSettingsModule/applyDefaultSettings");
    },

    openEditTriggerDialog(trigger) {
      this.$refs.triggerEditDialog.openDialog(trigger);
    },

    openDeleteTriggerDialog(trigger) {
      this.$refs.deleteDialog.openDialog({
        header: locService.localize("trigger_page.dialog.delete.title"),
        text: locService.localize("trigger_page.dialog.delete.text", [
          trigger.name,
        ]),
        submitText: locService.localize("btn.delete"),
        submitColor: "red",
        onSubmit: async () => {
          try {
            const result = await this.$store.dispatch(
              "triggersModule/deleteTrigger",
              trigger,
            );
            Vue.toasted.success(result, {
              duration: 5000,
            });
            this.reloadPage();
          } catch (e) {
            Vue.toasted.error(e, {
              duration: 5000,
            });
          }
        },
      });
    },

    reloadPage() {
      this.$store.dispatch("triggersModule/reloadPage");
    },

    updateRoutePath() {
      const params = new URLSearchParams();

      params.append("pageSize", this.pageSize);

      params.append("pageIndex", this.pageIndex);

      params.append("sort", JSON.stringify(this.sort));

      if (this.searchString) {
        params.append("searchString", this.searchString);
      }

      if (
        this.$route.query.pageSize != (params.get("pageSize") || undefined)
        || this.$route.query.pageIndex != (params.get("pageIndex") || undefined)
        || this.$route.query.searchString != (params.get("searchString") || undefined)
        || this.$route.query.sort != (params.get("sort") || undefined)
      ) {
        this.$router.push(`/triggers/?${params.toString()}`);
      }
    },

    onInputSearchString: lodash.debounce(function () {
      const newPage = { ...this.currentPage };

      newPage.searchString = this.searchString;

      this.$store.dispatch("triggersModule/loadPage", newPage);
      this.updateRoutePath();
    }, 600),
  },

  created() {
    getModule(TriggersModule, this.$store);

    this.$store.dispatch("triggerTableSettingsModule/loadDefaultSettingsFromStorage");

    const query = this.$route.query || {};

    if (!Object.keys(query).length) {
      this.$store.commit("triggerTableSettingsModule/applyDefaultSettings");
    } else {
      this.$store.commit("triggerTableSettingsModule/applyQuerySettings", query);

      if (query.searchString) {
        this.searchString = query.searchString;
      }
    }
  },
};
</script>

<style scoped lang="scss">
.trigger-table-display {
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.search-field {
  max-width: 250px;
}
</style>
